import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useState, useEffect } from 'react';
import { W as Wrapper, S as StyledLabelLine } from './Dropdown.styles-Wj3QvlFN.js';
import { D as DropdownContainer } from './DropdownContainer-D-zLrOLe.js';
import { D as DropdownButton } from './DropdownButton-i7KXWciN.js';
import { D as DropdownIsMobileProvider } from './DropdownIsMobileContext-DM1QP5jf.js';
import { D as DropdownContextProvider } from './DropdownContext-Cjb3JGE6.js';
import { D as DropdownItemDisplayName, a as DropdownDisplayName } from './displayNames-CI4WGOeZ.js';
import { d as defaultValueSerializer } from './utils-B_t3KuC-.js';
import { a as TaktIdConsumer, w as withTaktFallbackId } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { i as isString } from '../LocalModules/storm-ui-utils/dist/esm/isString-D4CFRAVS-D4CFRAVS.js';
import { c as composeRefs } from '../LocalModules/storm-ui-utils/dist/esm/composeRefs-CHPW6wJ8-CHPW6wJ8.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';
import { h as hasOwnProperty } from '../LocalModules/storm-ui-utils/dist/esm/hasOwnProperty-BBa9GG4X-BBa9GG4X.js';

const INPUT_VALUE_SEPARATOR = ',';
const findLabels = (children, result, isSelectedValue) => {
  React__default.Children.forEach(children, item => {
    /*
     * We use the `name` static member to determine if the child is a DropdownItem.
     * We can not directly check if it is the same instance of the component
     * because the hot loader will create new instances in dev mode. Then the comparison
     * will fail incorrectly.
     */
    const itemDisplayName = hasOwnProperty(item, 'type') && hasOwnProperty(item.type, 'displayName') ? item.type.displayName : '';
    if (itemDisplayName && itemDisplayName === DropdownItemDisplayName) {
      if (hasOwnProperty(item, 'props') && isSelectedValue(item.props.value)) {
        result.push({
          value: item === null || item === void 0 ? void 0 : item.props.value,
          label: item === null || item === void 0 ? void 0 : item.props.children
        });
      }
    } else if (hasOwnProperty(item, 'props') && item.props.children) {
      // Skip nested Dropdown
      if (itemDisplayName !== DropdownDisplayName) {
        findLabels(item.props.children, result, isSelectedValue);
      }
    }
  });
};
const defaultOnOverrideLabel = label => {
  if (Array.isArray(label)) {
    if (label.length === 0) {
      return 'Select Options';
    }
    if (label.length === 1) {
      return label[0];
    }
    return `(${label.length} selected) ${label.join(', ')}`;
  }
  return label;
};
const DropdownLabelLine = withTaktFallbackId(StyledLabelLine);
const Dropdown = ({
  children,
  selectedValue = '',
  selectedLabelOverride,
  onOverrideLabel = defaultOnOverrideLabel,
  forceOpen = false,
  onChange = noop,
  small = false,
  preRender,
  postRender,
  onOpen = noop,
  onClose = noop,
  inline = false,
  hideLabel = false,
  label = '',
  fullWidth = false,
  disabled = false,
  noWrapDropdownItems = false,
  focusSelected = true,
  error = false,
  shadowRoot,
  buttonRef,
  id,
  renderLabelStart,
  renderLabelEnd,
  transparentButton = false,
  secondaryViewCloseButtonLabel = 'Done',
  mobileFullScreen = false,
  name,
  multiple = false,
  maxHeight,
  taktId,
  taktValue,
  openDirection = 'bottom',
  forceDirection = false,
  popoverRef,
  isOpen,
  ...rest
}) => {
  const valueSerializer = defaultValueSerializer;
  const selectedLabelObjectsRef = useRef([]);
  const getCurrentLabels = () => {
    const isSelectedValue = currentValue => {
      if (Array.isArray(selectedValue)) {
        return selectedValue.some(value => valueSerializer(value) === valueSerializer(currentValue));
      }
      return valueSerializer(selectedValue) === valueSerializer(currentValue);
    };
    const currentLabelObjects = [];
    findLabels(children, currentLabelObjects, isSelectedValue);
    const newLabelObjects = [];
    const selectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
    selectedValues.forEach(value => {
      const currentLabel = currentLabelObjects.find(currentLabelObject => valueSerializer(value) === valueSerializer(currentLabelObject.value));
      if (currentLabel) {
        newLabelObjects.push({
          value: currentLabel.value,
          label: currentLabel.label
        });
      } else {
        const selected = selectedLabelObjectsRef.current.find(selectedLabelObject => valueSerializer(value) === valueSerializer(selectedLabelObject.value));
        if (selected) {
          newLabelObjects.push({
            value: selected.value,
            label: selected.label
          });
        }
      }
    });
    selectedLabelObjectsRef.current = newLabelObjects;
    return newLabelObjects.map(newLabelObject => newLabelObject.label);
  };
  const getButtonTextRefValue = () => {
    var _ref, _getCurrentLabels$;
    return (_ref = selectedLabelOverride || (onOverrideLabel === null || onOverrideLabel === void 0 ? void 0 : onOverrideLabel(multiple ? getCurrentLabels() : (_getCurrentLabels$ = getCurrentLabels()[0]) !== null && _getCurrentLabels$ !== void 0 ? _getCurrentLabels$ : '', selectedValue))) !== null && _ref !== void 0 ? _ref : '';
  };
  const toggleRef = useRef(null);
  const labelRef = useRef(null);
  const prevButtonTextRef = useRef(getButtonTextRefValue());
  const [isPopoverVisible, setIsPopoverVisible] = useState(() => {
    var _ref2;
    return (_ref2 = isOpen !== null && isOpen !== void 0 ? isOpen : forceOpen) !== null && _ref2 !== void 0 ? _ref2 : false;
  });
  useEffect(() => {
    if (forceOpen) {
      setIsPopoverVisible(true);
    } else if (isOpen !== undefined) {
      setIsPopoverVisible(isOpen);
    }
  }, [isOpen, forceOpen]);
  useEffect(() => {
    if (!isPopoverVisible && forceOpen) {
      setIsPopoverVisible(true);
    }
  }, [isPopoverVisible, forceOpen]);
  const handleChange = (value, event) => {
    onChange === null || onChange === void 0 || onChange(value, event);
  };
  const togglePopoverView = () => {
    if (!disabled && !forceOpen) {
      if (isOpen !== undefined) {
        // Controlled mode
        if (isOpen) {
          onClose();
        } else {
          onOpen();
        }
      } else {
        // Uncontrolled mode
        setIsPopoverVisible(prevState => {
          const nextState = !prevState;
          if (nextState) {
            onOpen();
          } else {
            onClose();
          }
          return nextState;
        });
      }
    }
  };
  const togglePopoverViewFromPopper = event => {
    var _labelRef$current;
    if (!event) {
      togglePopoverView();
    } else if (event && event.target instanceof Node && labelRef && !((_labelRef$current = labelRef.current) !== null && _labelRef$current !== void 0 && _labelRef$current.contains(event.target))) {
      togglePopoverView();
    }
  };
  const getButtonText = () => {
    if (isPopoverVisible) {
      return prevButtonTextRef.current;
    }
    prevButtonTextRef.current = getButtonTextRefValue();
    return prevButtonTextRef.current;
  };
  const buttonText = getButtonText();
  return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId(`dropdown${multiple ? '-multiple' : ''}`)
  }, ({
    getDataTaktAttributes
  }) => /*#__PURE__*/React__default.createElement(DropdownIsMobileProvider, {
    mobileFullScreen: mobileFullScreen
  }, /*#__PURE__*/React__default.createElement(Wrapper, {
    $fullWidth: fullWidth,
    $inline: inline
  }, label && /*#__PURE__*/React__default.createElement(DropdownLabelLine, _extends({}, getDataTaktAttributes({
    taktIdSuffix: 'label'
  }), {
    innerRef: labelRef,
    $inline: inline,
    hidden: hideLabel,
    renderLabelStart: renderLabelStart,
    renderLabelEnd: renderLabelEnd,
    onLabelClick: togglePopoverView
  }, isString(id) ? {
    htmlFor: id
  } : undefined), label), /*#__PURE__*/React__default.createElement(DropdownContextProvider, {
    multiple: multiple,
    id: id,
    label: label
  }, /*#__PURE__*/React__default.createElement(DropdownContainer, {
    secondaryViewCloseButtonLabel: secondaryViewCloseButtonLabel,
    anchorEl: toggleRef,
    isOpen: isPopoverVisible,
    togglePopoverViewFromPopper: togglePopoverViewFromPopper,
    shadowRoot: shadowRoot,
    trigger: triggerProps => /*#__PURE__*/React__default.createElement(DropdownButton, _extends({
      "aria-label": `${label}${buttonText ? `${label ? ' ' : ''}${buttonText}` : ''}`
    }, rest, {
      name: name
    }, getDataTaktAttributes({
      taktIdSuffix: 'trigger-button'
    }), {
      $inline: inline,
      disabled: disabled,
      small: small,
      onClick: togglePopoverView,
      buttonRef: composeRefs(buttonRef, toggleRef, triggerProps === null || triggerProps === void 0 ? void 0 : triggerProps.setTriggerElement),
      "aria-haspopup": "listbox",
      "aria-invalid": error,
      $error: error,
      variant: transparentButton ? 'transparent' : 'default'
    }, isString(id) ? {
      id
    } : undefined, {
      buttonText: buttonText,
      $fullWidth: fullWidth
    })),
    preRender: preRender,
    postRender: postRender,
    selectedValue: selectedValue,
    onChange: handleChange,
    noWrap: noWrapDropdownItems,
    focusSelected: focusSelected,
    forceOpen: forceOpen,
    maxHeight: maxHeight,
    popoverRef: popoverRef,
    openDirection: openDirection,
    forceDirection: forceDirection
  }, children)), /*#__PURE__*/React__default.createElement("input", {
    type: "hidden",
    name: name,
    value: Array.isArray(selectedValue) ? selectedValue.map(valueSerializer).join(INPUT_VALUE_SEPARATOR) : valueSerializer(selectedValue)
  }))));
};
Dropdown.displayName = DropdownDisplayName;

export { Dropdown as D };
//# sourceMappingURL=Dropdown-JKhB29j5.js.map
